import React from 'react';
import { FormGroup } from 'reactstrap';
import { faBuilding } from '@fortawesome/free-solid-svg-icons';
import { httpClient } from '@s20/services/httpClient';
import { BaseFormViewModel } from '../common/ViewModel';
import {
    onFieldChange,
    toasty,
    FormCheckbox,
    Input,
} from '../common/forms/FormElements';
import CommonContext, { ApiRoutes } from '../Common';
import SlideForm from '../common/forms/SlideForm';
import { handleFormSaveError } from '../common/forms/ValidationError';
import AddressInput from '../address/AddressInput';
import ValidatedSelect from '../common/forms/ValidatedSelect';
import { TipAlert } from '../common/TipAlert';

class TenantViewModel {
    id = 0;
    name = '';
    address1 = '';
    address2 = '';
    zip = '';
    state = 0;
    phone = '';
    city = '';
    countryCode = '';
    countryId = null;
    stateId = null;
    code = '';
    active = 0;
    sourceTenantId = null;
}

export class ManageTenant extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);
        this.formRef = React.createRef();

        const initialTenant = this.props.company ?? new TenantViewModel();

        this.state = {
            company: initialTenant,
            usStates: [],
            countries: [],
            tenants: [],
            ...new BaseFormViewModel(),
        };

        this.onChange = this.onChange.bind(this);
        this.resetForm = this.resetForm.bind(this);
        this.handleSaveError = this.handleSaveError.bind(this);
        this.onCountryChange = this.onCountryChange.bind(this);
        this.onStateChange = this.onStateChange.bind(this);
    }

    componentDidMount() {
        const { show } = this.props;
        this.loadCountries();
        this.loadTenants();
        if (show) {
            this.open();
        }
    }
    async loadCountries() {
        try {
            const [countries] = await Promise.all([
                httpClient.js(ApiRoutes.typeAheads.countries()),
            ]);

            this.setState({ countries });
        } catch (error) {
            console.error('Failed to load Countries:', error);
        }
    }

    async loadTenants() {
        try {
            const tenants = await httpClient.js(ApiRoutes.typeAheads.tenants());
            this.setState({ tenants });
        } catch (error) {
            console.error('Failed to load Tenants:', error);
        }
    }

    onSourceTenantChange = (selection) => {
        const { company } = this.state;
        company.sourceTenantId = selection?.value ?? null;
        this.setState({ company });
    };

    onCountryChange(selection) {
        const { company } = this.state;
        company.countryId = selection?.id;
        company.countryCode = selection?.value;
        if (!company.countryId) {
            company.stateId = '';
            company.country = '';
            company.state = '';
        }
        this.setState({ company });
    }

    onStateChange = (item) => {
        const { company } = { ...this.state };
        company.stateId = item?.id || '';
        company.state = item?.value || '';
        if (!company.stateId) {
            company.state = '';
        }
        this.setState({ company });
    };




    onChange = onFieldChange;

    onClose = () => {
        this.resetForm();
        this.props.toggleShow(false);
        this.context.setFormOpened(false);
        if (this.props.onClose) {
            this.props.onClose();
        }
    };

    onSubmit = async () => {
        const { company } = this.state;
        this.setState({ errors: {}, saving: true });



        const isUpdate = !!company.id && company.id > 0;

        const url = isUpdate
            ? ApiRoutes.tenant.byId(company.id)
            : ApiRoutes.tenant.create();
        const method = isUpdate ? httpClient.put : httpClient.post;

        const response = await method(url, company).catch(this.handleSaveError);

        if (response) {
            // On create the API returns the new tenant id; on update we already have it.
            const savedTenantId = isUpdate ? company.id : response;
            this.onClose();
            toasty.success(
                'Tenant Saved',
                'Tenant saved successfully.',
            );

            // After creating a new tenant, surface the onboarding checklist.
            if (!isUpdate && this.props.onCreated) {
                this.props.onCreated(savedTenantId, company.companyName);
            }
        }

        this.setState({ errors: {}, saving: false });
    };

    handleSaveError = (err) => handleFormSaveError(this, err);

    open = async (companyId) => {
        this.setState({ loading: true });
        this.resetForm();

        let company;

        if (companyId) {
            company = await httpClient.js(ApiRoutes.tenant.byId(companyId));
        } else {
            company = new TenantViewModel();
        }

        this.setState({ company }, () => {
            this.props.toggleShow(true);
            this.context.setFormOpened(true);

            this.setState({ loading: false });
        });
    };

    resetForm() {
        this.setState({
            formValidated: false,
        });
    }

    render() {
        const {
            formValidated, validationMessage, company, tenants,
        } = this.state;
        const isCreate = !(parseInt(company.id ?? 0) > 0);
        const formTitle = isCreate ? 'Add Tenant' : 'Edit Tenant';

        return (
            <SlideForm
                loading={this.state.loading}
                show={this.props.show}
                id="companyForm"
                formIcon={faBuilding}
                formTitle={formTitle}
                ref={this.formRef}
                setIsValidated={(value) => {
                    this.setState({ formValidated: value });
                }}
                isValidated={formValidated}
                onSubmit={this.onSubmit}
                onClose={this.onClose}
                errors={this.state.errors}
                error={validationMessage}
                saveDisabled={this.state.saving}
            >

                {isCreate && (
                    <>
                        <ValidatedSelect
                            id="sourceTenant"
                            name="sourceTenantId"
                            label="Copy data from tenant"
                            required
                            options={tenants}
                            value={tenants.find((t) => t.value === company.sourceTenantId) || null}
                            onChange={this.onSourceTenantChange}
                            error="Selecting a tenant to copy data from is required."
                        />
                        <TipAlert
                            containerClassName="mb-3"
                            label="The system will copy configuration (feature flags & settings, roles & groups, equipment types, and charge types) from the selected tenant into the tenant being created."
                        />
                    </>
                )}

                <Input
                    label="Company Name"
                    id="companyName"
                    name="company.companyName"
                    value={company.companyName || ''}
                    onChange={this.onChange}
                    placeholder="Enter the company name"
                    error="Company Name is required."
                    required
                />
                <Input
                    label="Company Code"
                    id="companyCode"
                    name="company.code"
                    value={company.code || ''}
                    onChange={this.onChange}
                    placeholder="Enter the company Code"
                    error="Company Code is required."
                    required
                />

                <AddressInput
                    className="company-address-input"
                    namePrefix="company"
                    onChange={this.onChange}
                    address={company}
                    onStateChanged={this.onStateChange}
                    onCountryChanged={this.onCountryChange}
                    showCountryInput
                    single
                />
                <Input
                    label="Phone"
                    id="companyPhone"
                    name="company.phone"
                    value={company.phone || ''}
                    onChange={this.onChange}
                    placeholder="Enter the phone number"
                    type="tel"
                />

                <FormGroup>
                    <FormCheckbox
                        id="isCompanyActive"
                        name="company.active"
                        type="checkbox"
                        checked={company.active}
                        onChange={this.onChange}
                        labelText="Active"
                    />
                </FormGroup>
            </SlideForm>
        );
    }
}
